import { useState, useEffect, useRef, useCallback } from "react";
import { TextField, Spinner, Icon } from "@medentic/polaris-react";
import { CheckCircleIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import { IBANValidationResponse, BankDetails } from "@shared/types/billingTypes";

export interface IbanFieldProps {
  value: string;
  onChange: (value: string) => void;
  onValidityChange?: (isValid: boolean) => void;
  label?: string;
  helpText?: string;
  required?: boolean;
  disabled?: boolean;
}

// Validate IBAN format and checksum using MOD-97 algorithm (ISO 7064)
const isValidIban = (iban: string): boolean => {
  if (!iban) return false;

  // Remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, "").toUpperCase();

  // Check length (15-34 characters)
  if (cleanIban.length < 15 || cleanIban.length > 34) {
    return false;
  }

  // IBAN starts with 2 letters (country code), 2 check digits, then alphanumeric BBAN
  const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
  if (!ibanRegex.test(cleanIban)) {
    return false;
  }

  // Move the first 4 characters to the end
  const rearranged = cleanIban.slice(4) + cleanIban.slice(0, 4);

  // Replace letters with numbers (A=10, B=11, ..., Z=35)
  let numericString = "";
  for (const char of rearranged) {
    if (char >= "A" && char <= "Z") {
      numericString += (char.charCodeAt(0) - 55).toString();
    } else {
      numericString += char;
    }
  }

  // Calculate MOD 97 using string-based arithmetic (to handle large numbers)
  let remainder = 0;
  for (const digit of numericString) {
    remainder = (remainder * 10 + parseInt(digit, 10)) % 97;
  }

  return remainder === 1;
};

const IbanField: React.FC<IbanFieldProps> = ({
  value,
  onChange,
  onValidityChange,
  label = "IBAN",
  helpText,
  required = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const { POST } = useApiRequest();
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [bankDetails, setBankDetails] = useState<BankDetails | null>(null);
  const [isIbanValid, setIsIbanValid] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastValidatedIban = useRef<string | null>(null);
  const isValidationInProgress = useRef<boolean>(false);
  const postRef = useRef(POST);

  // Keep POST ref up to date
  useEffect(() => {
    postRef.current = POST;
  }, [POST]);

  // Notify parent of validity changes
  useEffect(() => {
    onValidityChange?.(isIbanValid && !validationError);
  }, [isIbanValid, validationError, onValidityChange]);

  const validateIban = useCallback(async (iban: string) => {
    // Skip if already validated this IBAN or validation is in progress
    if (lastValidatedIban.current === iban || isValidationInProgress.current) {
      return;
    }

    isValidationInProgress.current = true;
    setIsValidating(true);
    try {
      const response = (await postRef.current(
        "/api/billing/validate-iban",
        { iban },
        false,
        true,
        true
      )) as IBANValidationResponse;
      // Mark as validated even if invalid, to prevent retries
      lastValidatedIban.current = iban;

      if (response.isValidIBAN) {
        setValidationError(null);
        setBankDetails(response.bankDetails || null);
        setIsIbanValid(true);
      } else {
        setValidationError(t("common.invalidIban"));
        setBankDetails(null);
        setIsIbanValid(false);
      }
    } catch {
      // On API error, mark as validated to prevent retries and toast spam
      // IBAN passed local validation, so consider it valid even if API fails
      lastValidatedIban.current = iban;
      setValidationError(null);
      setBankDetails(null);
      setIsIbanValid(true);
    } finally {
      setIsValidating(false);
      isValidationInProgress.current = false;
    }
  }, [t]);

  useEffect(() => {
    // Clear any existing debounce timer
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // Reset state if value is empty
    if (!value) {
      setValidationError(null);
      setBankDetails(null);
      setIsValidating(false);
      setIsIbanValid(false);
      lastValidatedIban.current = null;
      isValidationInProgress.current = false;
      return;
    }

    // Validate IBAN locally (format + checksum)
    if (!isValidIban(value)) {
      const cleanValue = value.replace(/\s/g, "").toUpperCase();

      // Show error if:
      // 1. Contains spaces or invalid characters (not alphanumeric)
      const hasInvalidChars =
        /[^A-Z0-9]/i.test(value) || value !== cleanValue;
      // 2. IBAN has 4+ chars but doesn't match basic prefix format (2 letters + 2 digits)
      const hasInvalidPrefix =
        cleanValue.length >= 4 && !/^[A-Z]{2}[0-9]{2}/.test(cleanValue);
      // 3. Any input longer than 4 chars that's not a valid IBAN
      const isIncompleteOrInvalid = cleanValue.length > 2;

      const showError =
        hasInvalidChars || hasInvalidPrefix || isIncompleteOrInvalid;

      setValidationError(showError ? t("common.invalidIban") : null);
      setBankDetails(null);
      setIsValidating(false);
      setIsIbanValid(false);
      if (showError) {
        lastValidatedIban.current = value;
      } else if (lastValidatedIban.current === value) {
        lastValidatedIban.current = null;
      }
      return;
    }

    // Skip if already validated this IBAN or validation is in progress
    if (
      lastValidatedIban.current === value ||
      isValidationInProgress.current
    ) {
      return;
    }

    // Debounce API call for valid IBANs to get bank details
    setIsValidating(true);
    debounceRef.current = setTimeout(() => {
      validateIban(value);
    }, 500);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [value, validateIban, t]);

  const handleChange = (newValue: string) => {
    // Remove spaces and convert to uppercase for consistency
    onChange(newValue.replace(/\s/g, "").toUpperCase());
  };

  const renderSuffix = () => {
    if (disabled) {
      return undefined;
    }
    if (isValidating) {
      return <Spinner size="small" />;
    }
    if (isIbanValid && !validationError) {
      if (bankDetails?.logo) {
        return (
          <img
            src={bankDetails.logo}
            alt={bankDetails.name}
            style={{ height: "20px", width: "auto" }}
          />
        );
      }
      return <Icon source={CheckCircleIcon} tone="success" />;
    }
    return undefined;
  };

  // Show bank name as help text if available and no custom helpText provided
  const displayHelpText = bankDetails?.name && !helpText ? bankDetails.name : helpText;

  return (
    <TextField
      label={label}
      value={value}
      onChange={handleChange}
      autoComplete="off"
      maxLength={34}
      error={validationError || undefined}
      helpText={displayHelpText}
      requiredIndicator={required}
      suffix={renderSuffix()}
      disabled={disabled}
    />
  );
};

export default IbanField;
